import React, { PureComponent } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import FieldModes from '../../../constants/FieldModes';
import ThemeIcons from '../../../theme/Icons';
import Button from '../../misc/Button';
import SignatureModal from '../../misc/SignatureModal';
import Tooltip from '../../misc/Tooltip';
import FieldTitle from './FieldTitle';
import FieldDecorators from './FieldDecorators';
import { StyledFieldHeader, isFieldHeaderEmpty } from './FieldHeader';

const StyledWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${(props) =>
    typeof props.margin === "number" ? `padding: ${props.margin}px;` : ""}
`;

const StyledSignatureWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: ${(props) => props.backgroundColor};
  border-radius: ${(props) => props.borderRadius}px;
  border: ${(props) => props.border};
  overflow: hidden;
`;

const StyledSignatureMeasurer = styled.div`
  width: 100%;
  height: 100%;
`;

const StyledImg = styled.img`
  width: ${(props) => (props.width ? `${props.width}px` : "100%")};
  height: ${(props) => (props.height ? `${props.height}px` : "100%")};
  object-fit: contain;
`;

const StyledSaveButton = styled.div`
  position: absolute;
  bottom: 6px;
  right: 6px;
`;

const StyledTooltipWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  z-index: 999999999999;
`;

class SignatureField extends PureComponent {
  static propTypes = {
    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,

    title: PropTypes.string,
    tipTitle: PropTypes.string,
    tipDescription: PropTypes.string,
    tipVisible: PropTypes.bool,

    value: PropTypes.string,
    disabled: PropTypes.bool,

    //Title styles
    titleDisplay: PropTypes.string,
    titleFontSize: PropTypes.number,
    titleFontColor: PropTypes.string,
    titleTextAlign: PropTypes.string,
    titleFontStyle: PropTypes.string,
    titleFontWeight: PropTypes.string,
    titleTextTransform: PropTypes.string,
    titleTextDecoration: PropTypes.string,
    titleTextOverflow: PropTypes.string,

    backgroundColor: PropTypes.string,
    borderWidth: PropTypes.number,
    borderColor: PropTypes.string,
    margin: PropTypes.number,

    //Action handlers
    onLoadCompleted: PropTypes.func,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onRef: PropTypes.func, //Used for testing purposes
  };

  constructor(props) {
    super(props);
    this.state = { displayModal: false, signatureLoaded: false };
  }

  componentDidUpdate() {

    const { onLoadCompleted } = this.props;
    const { signatureLoaded } = this.state;
    
    if (signatureLoaded && onLoadCompleted) onLoadCompleted();

  }

  componentDidMount() {
    const { onLoadCompleted, value } = this.props;
    if (onLoadCompleted && !value) onLoadCompleted();
  }

  render() {
    const {
      fieldId,
      fieldPositionId,
      fieldIdentifier,

      title,
      tipTitle,
      tipDescription,
      tipVisible,
      value,
      disabled,
      onRef,

      fieldMode,
      hideButton,

      //Title styles
      titleDisplay,
      titleFontSize,
      titleFontColor,
      titleTextAlign,
      titleFontStyle,
      titleFontWeight,
      titleTextTransform,
      titleTextDecoration,
      titleTextOverflow,

      primaryDisplayOnly,
      isMobileView,

      //Styles
      borderRadius,
      borderWidth,
      borderColor,
      backgroundColor,
      margin,

      maxImageWidth,
      maxImageHeight,
      decorators,
      onDecoratorClick,
    } = this.props;

    const { displayModal } = this.state;

    const bodyNode = !value ? null : (
      <StyledImg
        src={value}
        data-testid={`${fieldPositionId}-signature-field-img`}
        width={maxImageWidth}
        height={maxImageHeight}
        onLoad={this.handleImageLoad}
        onError={this.handleImageLoad}
      />
    );

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a two tone color if you use border-color and border-width
     * indvidually.
     */
    const border = `${borderWidth}px solid ${borderColor}`;

    const getTooltip = () => {
      if (fieldMode === FieldModes.readonly) return null;

      return (
        <StyledTooltipWrapper>
          <Tooltip
            fieldId={fieldId}
            title={tipTitle}
            description={tipDescription}
            tipVisible={tipVisible}
          />
        </StyledTooltipWrapper>
      );
    };

    return (
      <StyledWrapper {...{ margin }}>
        <StyledFieldHeader $empty={isFieldHeaderEmpty({ title, titleDisplay, decorators, fieldMode })}>
          <FieldTitle
            {...{
              title,
              titleDisplay,
              titleFontSize,
              titleFontColor,
              titleTextAlign,
              titleFontStyle,
              titleFontWeight,
              titleTextTransform,
              titleTextDecoration,
              titleTextOverflow,
            }}
          />
          <FieldDecorators
            decorators={decorators}
            fieldMode={fieldMode}
            fieldId={fieldId}
            fieldIdentifier={fieldIdentifier}
            fieldPositionId={fieldPositionId}
            onDecoratorClick={onDecoratorClick}
          />
        </StyledFieldHeader>
        {getTooltip()}

        <StyledSignatureWrapper
          data-testid={`${fieldPositionId}-signature-field-wrapper`}
          onClick={() => {
            this.handleFocus();
            if (!disabled) this.setState({ displayModal: true });
          }}
          {...{
            border,
            borderRadius,
            backgroundColor,
          }}
        >
          <StyledSignatureMeasurer>
            {bodyNode}
            {disabled || hideButton || fieldMode === FieldModes.edit ? null : (
              <StyledSaveButton>
                <Button
                  solid
                  size="sm"
                  icon={ThemeIcons.fields.signature}
                  text="Sign"
                  onClick={() => {
                    this.setState({ displayModal: true });
                  }}
                />
              </StyledSaveButton>
            )}
          </StyledSignatureMeasurer>
        </StyledSignatureWrapper>
        <SignatureModal
          display={displayModal}
          width={isMobileView ? 290 : null}
          primaryDisplayOnly={primaryDisplayOnly}
          onHide={() => {
            this.setState({ displayModal: false });
            this.handleBlur();
          }}
          onSubmit={this.handleSubmit}
          onRef={onRef}
        />
      </StyledWrapper>
    );
  }

  handleImageLoad = () => {
    if (!this.state.signatureLoaded) {
      this.setState({ signatureLoaded: true });
    }
  }

  handleFocus = () => {
    const { onFocus, fieldId, fieldIdentifier, fieldPositionId, onBlur, disabled } = this.props;

    if (onFocus && !disabled) {
      onFocus(fieldId, fieldIdentifier, fieldPositionId, {
        blur: () => {
          if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});

          this.setState({ displayModal: false });
        },
      });
    }
  };

  handleBlur = () => {
    const { onBlur, fieldId, fieldIdentifier, fieldPositionId } = this.props;
    if (onBlur) onBlur(fieldId, fieldIdentifier, fieldPositionId, {});
  };

  handleSubmit = (dataUrl, signer) => {
    const { onChange, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    if (onChange)
      onChange(fieldId, fieldIdentifier, fieldPositionId, dataUrl, signer);

    this.handleBlur();

    this.setState({ displayModal: false });
  };
}

export default SignatureField;
